/**
 * Mockup de Administración de GPS — puerto JS del módulo TS de la nueva front
 * (Beartrack/src/lib/mock/gps.ts). Datos curados (no aleatorios) para que el
 * demo se vea creíble; flota ficticia de courier de última milla en Santiago.
 *
 * Timestamps relativos al momento de carga del módulo → "último reporte"
 * siempre coherente con la hora actual del navegador.
 *
 * TODO(back): reemplazar `gpsApi` por llamadas reales al MS-GPS-TRACKER cuando
 * el back exponga los endpoints contractuales (ver FORECASTING-BACKEND.md
 * pattern). Por ahora todo es mock 100% client-side.
 */

import {
  formatRelative as formatRelativeBase,
  formatDateTimeShort,
  formatDayMonth,
} from '../../../utils/formatters';

// ── Catálogo de metadatos visuales para estados / severidades ───────────────
// `hex` es para la capa Leaflet (capa fija, no theme-able). El resto usa
// tokens semánticos del design system (status-*, brand-*).

export const estadoMeta = {
  movimiento: { label: 'En movimiento', tone: 'success', hex: '#16A34A' },
  detenido:   { label: 'Detenido',      tone: 'info',    hex: '#0080FF' },
  inactivo:   { label: 'Inactivo',      tone: 'warning', hex: '#EA580C' },
  sin_senal:  { label: 'Sin señal',     tone: 'neutral', hex: '#94A3B8' },
};

export const severidadTone = {
  critica: 'error', alta: 'warning', media: 'info', info: 'neutral',
};

export const severidadLabel = {
  critica: 'Crítica', alta: 'Alta', media: 'Media', info: 'Info',
};

export const alarmaLabel = {
  exceso_velocidad:    'Exceso de velocidad',
  geocerca_entrada:    'Entrada a geocerca',
  geocerca_salida:     'Salida de geocerca',
  bateria_desconectada:'Batería desconectada',
  panico:              'Botón de pánico',
  frenada_brusca:      'Frenada brusca',
  aceleracion_brusca:  'Aceleración brusca',
  parada_prolongada:   'Parada prolongada',
  sin_senal:           'Pérdida de señal',
  mantenimiento:       'Mantenimiento',
};

export const comandoLabel = {
  motor_bloquear:      'Bloquear motor',
  motor_desbloquear:   'Desbloquear motor',
  ubicacion_solicitar: 'Solicitar ubicación',
  reiniciar:           'Reiniciar equipo',
  reporte_intervalo:   'Cambiar intervalo de reporte',
  corte_combustible:   'Corte de combustible',
};

export const comandoEstadoMeta = {
  pendiente: { label: 'Pendiente', tone: 'neutral' },
  enviado:   { label: 'Enviado',   tone: 'info' },
  entregado: { label: 'Entregado', tone: 'info' },
  ejecutado: { label: 'Ejecutado', tone: 'success' },
  fallido:   { label: 'Fallido',   tone: 'error' },
};

export function rumboCardinal(deg) {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  return dirs[Math.round(deg / 45) % 8];
}

export function voltajeTone(v) {
  if (v == null) return 'neutral';
  if (v < 11.8) return 'error';
  if (v < 12.4) return 'warning';
  return 'success';
}

// ── Helpers de fecha ────────────────────────────────────────────────────────
const now = Date.now();
const minAgo = (m) => new Date(now - m * 60_000).toISOString();
const daysAgo = (d) => new Date(now - d * 86_400_000).toISOString();

// ── Specs base (mismos datos que la Next.js front) ──────────────────────────
const SPECS = [
  { imei: '356938035643809', alias: 'Furgón 01', patente: 'JZLZ·26', marca: 'Peugeot', modelo: 'Partner', tipo: 'furgon',
    conductor: 'Manuel Rojas', modeloGps: 'Teltonika FMC130', protocolo: 'teltonika', operador: 'Entel',
    estado: 'movimiento', vel: 54, rumbo: 78, lat: -33.4172, lng: -70.5476, direccion: 'Av. Las Condes 9420, Las Condes',
    reporteMin: 0, bateria: 96, voltaje: 13.9, conectada: true, senal: 4, sats: 11, odo: 84210, velMax: 120,
    geocercas: ['gc_01'], dist: 142 },
  { imei: '356938035641102', alias: 'Camión 3.5T', patente: 'LGYW·41', marca: 'Mitsubishi', modelo: 'Canter', tipo: 'camion-3.5t',
    conductor: 'Roberto Vargas', modeloGps: 'Teltonika FMC150', protocolo: 'teltonika', operador: 'Entel',
    estado: 'movimiento', vel: 118, rumbo: 190, lat: -33.5110, lng: -70.7580, direccion: 'Autopista Central km 14, Maipú',
    reporteMin: 0, bateria: 88, voltaje: 14.1, conectada: true, senal: 3, sats: 9, odo: 152980, velMax: 100,
    alarmas: ['exceso_velocidad'], geocercas: [], dist: 208 },
  { imei: '861234567890123', alias: 'Furgón 02', patente: 'GXWR·64', marca: 'Hyundai', modelo: 'H1', tipo: 'furgon',
    conductor: 'Jorge Tapia', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Movistar',
    estado: 'movimiento', vel: 38, rumbo: 12, lat: -33.3636, lng: -70.7286, direccion: 'Av. Américo Vespucio 1500, Huechuraba',
    reporteMin: 1, bateria: 72, voltaje: 13.7, conectada: true, senal: 4, sats: 12, odo: 61040, velMax: 120,
    geocercas: ['gc_02'], dist: 96 },
  { imei: '356938035649921', alias: 'Auto ejecutivo', patente: 'KKVG·24', marca: 'Hyundai', modelo: 'Grand i10', tipo: 'auto',
    conductor: 'Camila Soto', modeloGps: 'Queclink GV300', protocolo: 'queclink', operador: 'Entel',
    estado: 'movimiento', vel: 61, rumbo: 245, lat: -33.4314, lng: -70.6093, direccion: 'Av. Providencia 2200, Providencia',
    reporteMin: 0, bateria: 91, voltaje: 13.8, conectada: true, senal: 4, sats: 10, odo: 39870, velMax: 120,
    geocercas: ['gc_01'], dist: 77 },
  { imei: '861234567891777', alias: 'Furgón 03', patente: 'KPTP·78', marca: 'Renault', modelo: 'Master', tipo: 'furgon',
    conductor: 'Pedro Núñez', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Claro',
    estado: 'movimiento', vel: 27, rumbo: 320, lat: -33.5226, lng: -70.5996, direccion: 'Av. La Florida 9800, La Florida',
    reporteMin: 2, bateria: 64, voltaje: 13.6, conectada: true, senal: 3, sats: 8, odo: 112300, velMax: 120,
    geocercas: [], dist: 134 },
  { imei: '356938035640048', alias: 'Camión mayor', patente: 'LRCL·31', marca: 'Hyundai', modelo: 'HD 35', tipo: 'camion-mayor',
    conductor: 'Felipe Araya', modeloGps: 'Teltonika FMC150', protocolo: 'teltonika', operador: 'Entel',
    estado: 'movimiento', vel: 44, rumbo: 100, lat: -33.4569, lng: -70.5950, direccion: 'Av. Tobalaba 4500, Peñalolén',
    reporteMin: 1, bateria: 80, voltaje: 14.0, conectada: true, senal: 4, sats: 11, odo: 198450, velMax: 90,
    geocercas: ['gc_03'], dist: 176 },
  { imei: '861234567892555', alias: 'Furgón 04', patente: 'GHDP·65', marca: 'Peugeot', modelo: 'Boxer', tipo: 'furgon',
    conductor: 'Daniela Pinto', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Movistar',
    estado: 'movimiento', vel: 49, rumbo: 60, lat: -33.3823, lng: -70.5763, direccion: 'Camino El Alba 11000, Las Condes',
    reporteMin: 0, bateria: 77, voltaje: 13.9, conectada: true, senal: 4, sats: 10, odo: 70220, velMax: 120,
    geocercas: ['gc_01'], dist: 88 },

  { imei: '356938035648130', alias: 'Furgón 05', patente: 'JZRC·18', marca: 'Mercedes-Benz', modelo: 'Sprinter', tipo: 'furgon',
    conductor: 'Andrés Lillo', modeloGps: 'Teltonika FMC130', protocolo: 'teltonika', operador: 'Entel',
    estado: 'detenido', vel: 0, rumbo: 0, lat: -33.4534, lng: -70.6921, direccion: 'Hub Estación Central, Av. Ecuador 3000',
    reporteMin: 1, bateria: 84, voltaje: 13.8, conectada: true, senal: 4, sats: 12, odo: 90120, velMax: 120,
    geocercas: ['gc_04'], dist: 52 },
  { imei: '861234567893001', alias: 'Camión 3.5T (2)', patente: 'KSRJ·19', marca: 'Mitsubishi', modelo: 'Canter', tipo: 'camion-3.5t',
    conductor: 'Sofía Vera', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Claro',
    estado: 'detenido', vel: 0, rumbo: 0, lat: -33.4434, lng: -70.7570, direccion: 'Bodega Pudahuel, Camino a Lonquén 200',
    reporteMin: 3, bateria: 58, voltaje: 13.5, conectada: true, senal: 3, sats: 7, odo: 143780, velMax: 100,
    geocercas: ['gc_04'], dist: 61 },
  { imei: '356938035647441', alias: 'Auto soporte', patente: 'GXGR·16', marca: 'Suzuki', modelo: 'Alto K10', tipo: 'auto',
    modeloGps: 'Queclink GV300', protocolo: 'queclink', operador: 'Entel',
    estado: 'detenido', vel: 0, rumbo: 0, lat: -33.4172, lng: -70.6044, direccion: 'Taller Central OTS, Av. Vicuña Mackenna 1200',
    reporteMin: 5, bateria: 90, voltaje: 13.9, conectada: true, senal: 4, sats: 9, odo: 28510, velMax: 120,
    geocercas: ['gc_03'], dist: 33 },
  { imei: '861234567894200', alias: 'Furgón 06', patente: 'LFYR·73', marca: 'Peugeot', modelo: 'Partner', tipo: 'furgon',
    conductor: 'Ignacio Bravo', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Movistar',
    estado: 'detenido', vel: 0, rumbo: 0, lat: -33.5226, lng: -70.5996, direccion: 'Av. Vicuña Mackenna 7500, La Florida',
    reporteMin: 2, bateria: 69, voltaje: 13.7, conectada: true, senal: 3, sats: 8, odo: 64890, velMax: 120,
    geocercas: [], dist: 71 },
  { imei: '356938035646852', alias: 'Furgón 07', patente: 'PTHG·46', marca: 'Peugeot', modelo: 'Traveller', tipo: 'furgon',
    conductor: 'Valentina Mura', modeloGps: 'Teltonika FMC130', protocolo: 'teltonika', operador: 'Entel',
    estado: 'detenido', vel: 0, rumbo: 0, lat: -33.4012, lng: -70.5810, direccion: 'Parque Arauco, Av. Kennedy 5400, Las Condes',
    reporteMin: 4, bateria: 82, voltaje: 13.8, conectada: true, senal: 4, sats: 11, odo: 41250, velMax: 120,
    geocercas: ['gc_01'], dist: 64 },

  { imei: '861234567895100', alias: 'Furgón 08', patente: 'JZPD·27', marca: 'Hyundai', modelo: 'H1', tipo: 'furgon',
    conductor: 'Cristóbal Díaz', modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Claro',
    estado: 'inactivo', vel: 0, rumbo: 0, lat: -33.4889, lng: -70.6371, direccion: 'San Joaquín, Av. Departamental 1100',
    reporteMin: 38, bateria: 100, voltaje: 12.5, conectada: true, senal: 3, sats: 6, odo: 88340, velMax: 120,
    geocercas: [], dist: 12 },
  { imei: '356938035645263', alias: 'Camión 3.5T (3)', patente: 'KKPJ·29', marca: 'JAC', modelo: 'N56', tipo: 'camion-3.5t',
    modeloGps: 'Teltonika FMC150', protocolo: 'teltonika', operador: 'Entel',
    estado: 'inactivo', vel: 0, rumbo: 0, lat: -33.5630, lng: -70.5800, direccion: 'Puente Alto, Av. Concha y Toro 2400',
    reporteMin: 95, bateria: 100, voltaje: 12.4, conectada: true, senal: 2, sats: 5, odo: 167420, velMax: 100,
    geocercas: [], dist: 0 },
  { imei: '861234567896320', alias: 'Furgón 09', patente: 'LBDB·82', marca: 'Renault', modelo: 'Master', tipo: 'furgon',
    modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Movistar',
    estado: 'inactivo', vel: 0, rumbo: 0, lat: -33.4434, lng: -70.6512, direccion: 'Santiago Centro, Av. Matucana 800',
    reporteMin: 142, bateria: 100, voltaje: 12.6, conectada: true, senal: 3, sats: 7, odo: 59870, velMax: 120,
    geocercas: ['gc_05'], dist: 18 },
  { imei: '356938035644174', alias: 'Auto repuesto', patente: 'KLRR·22', marca: 'Chevrolet', modelo: 'Spark', tipo: 'auto',
    modeloGps: 'Coban TK403A', protocolo: 'gps103', operador: 'WOM',
    estado: 'inactivo', vel: 0, rumbo: 0, lat: -33.3636, lng: -70.7286, direccion: 'Quilicura, Av. Manuel Antonio Matta 950',
    reporteMin: 220, bateria: 100, voltaje: 12.3, conectada: true, senal: 2, sats: 4, odo: 33990, velMax: 120,
    geocercas: [], dist: 5 },
  { imei: '861234567897440', alias: 'Furgón 10', patente: 'GHTR·52', marca: 'Peugeot', modelo: 'Boxer', tipo: 'furgon',
    modeloGps: 'Concox GT06N', protocolo: 'gt06', operador: 'Claro',
    estado: 'inactivo', vel: 0, rumbo: 0, lat: -33.4789, lng: -70.5421, direccion: 'La Reina, Av. Larraín 9200',
    reporteMin: 64, bateria: 100, voltaje: 12.7, conectada: true, senal: 4, sats: 9, odo: 47600, velMax: 120,
    geocercas: ['gc_03'], dist: 9 },

  { imei: '356938035643500', alias: 'Camión mayor (2)', patente: 'PTRJ·08', marca: 'Mercedes-Benz', modelo: 'Atego', tipo: 'camion-mayor',
    modeloGps: 'Teltonika FMC150', protocolo: 'teltonika', operador: 'Entel',
    estado: 'sin_senal', vel: 0, rumbo: 0, lat: -33.4534, lng: -70.6650, direccion: 'Última posición: Independencia, Av. El Salto 200',
    reporteMin: 410, bateria: 0, voltaje: undefined, conectada: false, senal: 0, sats: 0, odo: 210500, velMax: 90,
    alarmas: ['sin_senal', 'bateria_desconectada'], geocercas: [], dist: 0 },
  { imei: '861234567898560', alias: 'Furgón 11', patente: 'JZGS·80', marca: 'Citroën', modelo: 'Jumper', tipo: 'furgon',
    modeloGps: 'Coban TK403A', protocolo: 'gps103', operador: 'WOM',
    estado: 'sin_senal', vel: 0, rumbo: 0, lat: -33.5950, lng: -70.7020, direccion: 'Última posición: San Bernardo, Av. Colón 1500',
    reporteMin: 1305, bateria: 0, conectada: false, senal: 0, sats: 0, odo: 78940, velMax: 120,
    alarmas: ['sin_senal'], geocercas: [], dist: 0 },
  { imei: '356938035642811', alias: 'Furgón 12', patente: 'LSPK·39', marca: 'Hyundai', modelo: 'H1', tipo: 'furgon',
    modeloGps: 'Queclink GV300', protocolo: 'queclink', operador: 'Movistar',
    estado: 'sin_senal', vel: 0, rumbo: 0, lat: -33.3290, lng: -70.6940, direccion: 'Última posición: Conchalí, Av. Independencia 4800',
    reporteMin: 2880, bateria: 0, conectada: false, senal: 0, sats: 0, odo: 55120, velMax: 120,
    alarmas: ['sin_senal'], geocercas: [], dist: 0 },
  { imei: '861234567899680', alias: 'Sin instalar', patente: '— · —', marca: 'Stock', modelo: 'Bodega', tipo: 'auto',
    modeloGps: 'Teltonika FMC130', protocolo: 'teltonika', operador: 'Entel',
    estado: 'sin_senal', vel: 0, rumbo: 0, lat: -33.4489, lng: -70.6693, direccion: 'En bodega — sin vincular a vehículo',
    reporteMin: 100000, bateria: 100, conectada: false, senal: 0, sats: 0, odo: 0, velMax: 120,
    geocercas: [], dist: 0 },
];

// ── Devices derivados ───────────────────────────────────────────────────────
export const gpsDevices = SPECS.map((s, i) => ({
  id: `gps_${String(i + 1).padStart(3, '0')}`,
  imei: s.imei,
  alias: s.alias,
  modelo: s.modeloGps,
  protocolo: s.protocolo,
  firmware: s.protocolo === 'teltonika' ? '03.28.07.Rev.00' : s.protocolo === 'queclink' ? 'R3.2.0' : '8.21',
  sim: {
    iccid: `8956${s.operador === 'Entel' ? '03' : s.operador === 'Movistar' ? '07' : s.operador === 'Claro' ? '10' : '21'}${s.imei.slice(4, 14)}`,
    operador: s.operador,
    numero: `+569 ${s.imei.slice(5, 9)} ${s.imei.slice(9, 13)}`,
    planMb: 50,
    consumoMb: Math.round((s.dist / 250) * 38 + 4),
  },
  patente: s.patente,
  vehiculoMarca: s.marca,
  vehiculoModelo: s.modelo,
  vehiculoTipo: s.tipo,
  conductorNombre: s.conductor,
  estado: s.estado,
  encendido: s.estado === 'movimiento' || s.estado === 'detenido',
  velocidadKmh: s.vel,
  rumbo: s.rumbo,
  pos: { lat: s.lat, lng: s.lng },
  direccion: s.direccion,
  ultimoReporte: minAgo(s.reporteMin),
  detenidoDesde: s.estado === 'detenido' || s.estado === 'inactivo' ? minAgo(s.reporteMin + 14) : undefined,
  bateriaPct: s.bateria,
  bateriaExternaV: s.voltaje,
  bateriaConectada: s.conectada,
  senalGsm: s.senal,
  satelites: s.sats,
  odometroKm: s.odo,
  velocidadMaxKmh: s.velMax,
  geocercasIds: s.geocercas ?? [],
  config: {
    excesoVelocidadKmh: s.velMax,
    geocercas: (s.geocercas ?? []).length > 0,
    bateriaDesconectada: true,
    panico: s.tipo !== 'auto',
    paradaProlongadaMin: 45,
    conduccionAgresiva: true,
  },
  alarmasActivas: s.alarmas ?? [],
  instaladoEl: daysAgo(120 + i * 11),
  resumenDia: {
    distanciaKm: s.dist,
    excesosVelocidad: (s.alarmas?.includes('exceso_velocidad')) ? 3 : s.dist > 120 ? 1 : 0,
    eventosGeocerca: (s.geocercas ?? []).length > 0 ? 2 + (i % 3) : 0,
    frenadasBruscas: s.estado === 'movimiento' ? i % 4 : 0,
    aceleracionesBruscas: s.estado === 'movimiento' ? (i + 1) % 3 : 0,
    encendidos: s.dist > 0 ? 2 + (i % 4) : 0,
    tiempoEncendidoMin: s.dist > 0 ? Math.round(s.dist * 1.6) : 0,
  },
}));

export const geocercas = [
  { id: 'gc_01', nombre: 'Zona Oriente (Las Condes)', tipo: 'circular', color: '#0080FF',
    centro: { lat: -33.4080, lng: -70.5670 }, radioM: 3200, regla: 'ambos', alertaActiva: true,
    dispositivosDentro: 3, dispositivosAsignados: 6, descripcion: 'Cobertura premium nororiente.' },
  { id: 'gc_02', nombre: 'Zona Norte (Huechuraba)', tipo: 'circular', color: '#16A34A',
    centro: { lat: -33.3636, lng: -70.7100 }, radioM: 2600, regla: 'salida', alertaActiva: true,
    dispositivosDentro: 1, dispositivosAsignados: 2, descripcion: 'Avisar al salir del polígono asignado.' },
  { id: 'gc_03', nombre: 'Taller Central OTS', tipo: 'circular', color: '#F97316',
    centro: { lat: -33.4172, lng: -70.6044 }, radioM: 400, regla: 'entrada', alertaActiva: true,
    dispositivosDentro: 1, dispositivosAsignados: 3, descripcion: 'Notificar ingreso a mantenimiento.' },
  { id: 'gc_04', nombre: 'Hub Estación Central', tipo: 'circular', color: '#9333EA',
    centro: { lat: -33.4534, lng: -70.6921 }, radioM: 600, regla: 'ambos', alertaActiva: true,
    dispositivosDentro: 2, dispositivosAsignados: 5, descripcion: 'Control de entradas/salidas de bodega.' },
  { id: 'gc_05', nombre: 'Perímetro restringido (Centro)', tipo: 'poligono', color: '#DC2626',
    poligono: [
      { lat: -33.4360, lng: -70.6580 }, { lat: -33.4360, lng: -70.6420 },
      { lat: -33.4520, lng: -70.6420 }, { lat: -33.4520, lng: -70.6580 },
    ],
    regla: 'entrada', alertaActiva: true, dispositivosDentro: 1, dispositivosAsignados: 1,
    descripcion: 'Zona de acceso vehicular restringido en horario punta.' },
];

export const gpsAlarmas = [
  { id: 'al_01', tipo: 'exceso_velocidad', severidad: 'alta', deviceId: 'gps_002', patente: 'LGYW·41', alias: 'Camión 3.5T',
    mensaje: 'Velocidad 118 km/h en zona de 100 km/h', at: minAgo(2),
    pos: { lat: -33.5110, lng: -70.7580 }, direccion: 'Autopista Central km 14, Maipú', valor: '118 km/h', resuelta: false },
  { id: 'al_02', tipo: 'bateria_desconectada', severidad: 'critica', deviceId: 'gps_018', patente: 'PTRJ·08', alias: 'Camión mayor (2)',
    mensaje: 'Se detectó corte de alimentación del equipo', at: minAgo(18),
    pos: { lat: -33.4534, lng: -70.6650 }, direccion: 'Independencia, Av. El Salto 200', valor: '0.0 V', resuelta: false },
  { id: 'al_03', tipo: 'sin_senal', severidad: 'alta', deviceId: 'gps_019', patente: 'JZGS·80', alias: 'Furgón 11',
    mensaje: 'Sin reporte hace más de 21 horas', at: minAgo(60),
    pos: { lat: -33.5950, lng: -70.7020 }, direccion: 'San Bernardo, Av. Colón 1500', resuelta: false },
  { id: 'al_04', tipo: 'geocerca_salida', severidad: 'media', deviceId: 'gps_003', patente: 'GXWR·64', alias: 'Furgón 02',
    mensaje: 'Salió de "Zona Norte (Huechuraba)"', at: minAgo(34), geocercaNombre: 'Zona Norte (Huechuraba)',
    pos: { lat: -33.3636, lng: -70.7286 }, direccion: 'Av. Américo Vespucio 1500, Huechuraba', resuelta: false },
  { id: 'al_05', tipo: 'parada_prolongada', severidad: 'media', deviceId: 'gps_013', patente: 'JZPD·27', alias: 'Furgón 08',
    mensaje: 'Detenido 38 min en zona no operativa', at: minAgo(40),
    pos: { lat: -33.4889, lng: -70.6371 }, direccion: 'San Joaquín, Av. Departamental 1100', valor: '38 min', resuelta: false },
  { id: 'al_06', tipo: 'frenada_brusca', severidad: 'info', deviceId: 'gps_001', patente: 'JZLZ·26', alias: 'Furgón 01',
    mensaje: 'Frenada brusca detectada (-0.62 g)', at: minAgo(72),
    pos: { lat: -33.4172, lng: -70.5476 }, direccion: 'Av. Las Condes 9420, Las Condes', valor: '-0.62 g', resuelta: true },
  { id: 'al_07', tipo: 'geocerca_entrada', severidad: 'info', deviceId: 'gps_010', patente: 'GXGR·16', alias: 'Auto soporte',
    mensaje: 'Ingresó a "Taller Central OTS"', at: minAgo(110), geocercaNombre: 'Taller Central OTS',
    pos: { lat: -33.4172, lng: -70.6044 }, direccion: 'Av. Vicuña Mackenna 1200', resuelta: true },
  { id: 'al_08', tipo: 'panico', severidad: 'critica', deviceId: 'gps_005', patente: 'KPTP·78', alias: 'Furgón 03',
    mensaje: 'Botón de pánico activado por el conductor', at: minAgo(186),
    pos: { lat: -33.5226, lng: -70.5996 }, direccion: 'Av. La Florida 9800, La Florida', resuelta: true },
];

export const gpsComandos = [
  { id: 'cmd_01', deviceId: 'gps_001', tipo: 'ubicacion_solicitar', estado: 'ejecutado', at: minAgo(6), por: 'G. Morales' },
  { id: 'cmd_02', deviceId: 'gps_018', tipo: 'reiniciar', estado: 'fallido', at: minAgo(22), por: 'G. Morales', detalle: 'Sin respuesta del equipo' },
  { id: 'cmd_03', deviceId: 'gps_002', tipo: 'reporte_intervalo', estado: 'entregado', at: minAgo(90), por: 'Central', detalle: 'Intervalo 10s' },
  { id: 'cmd_04', deviceId: 'gps_005', tipo: 'motor_bloquear', estado: 'ejecutado', at: minAgo(180), por: 'Seguridad' },
  { id: 'cmd_05', deviceId: 'gps_005', tipo: 'motor_desbloquear', estado: 'ejecutado', at: minAgo(120), por: 'Seguridad' },
];

// ── API mock — fachada idéntica al cliente real para facilitar cableo ───────
// TODO(back): reemplazar por fetch al MS-GPS-TRACKER (mismas shapes).
export const gpsApi = {
  devices: () => Promise.resolve(gpsDevices),
  geocercas: () => Promise.resolve(geocercas),
  alarmas: () => Promise.resolve(gpsAlarmas),
  comandos: (deviceId) => Promise.resolve(gpsComandos.filter((c) => c.deviceId === deviceId)),
};

// ── Helpers de formato ──────────────────────────────────────────────────────
// `formatDateTime`/`formatRelative` se consolidaron en utils/formatters (fuente
// única). Se conservan los nombres exportados y el comportamiento original
// (granularidad 'hace N s', fecha absoluta 'día mes', '—' para nulos) para que
// DeviceList/DeviceDetailPanel/GpsDispositivos/GpsAlarmas sigan funcionando.
export function formatNumber(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-CL').format(n);
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  return formatDateTimeShort(iso);
}

export function formatRelative(iso) {
  return formatRelativeBase(iso, {
    seconds: true,
    dayWords: false,
    absolute: formatDayMonth,
    invalid: '—',
  });
}
